import React from 'react';
import { Trash2, Calendar, Building, Tent, FileText, Image, File, Download, ChevronDown, ChevronUp } from 'lucide-react';
import DragDropZone from '../DragDropZone';

export default function GigCard({
  gig,
  isExpanded,
  onToggleExpand,
  onDelete,
  onSetlistUpload,
  onPhotoUpload,
  onDeletePhoto,
  loading,
  isMobile,
  isPast
}) {
  const isFestival = gig.locationType === 'festival' || gig.festivalId;

  const photosArray = gig.photos
    ? Object.keys(gig.photos).map(key => ({ id: key, ...gig.photos[key] }))
    : [];

  return (
    <div className={`card-3d border rounded-card overflow-hidden transition-all ${
      isPast ? 'border-transparent opacity-60' : 'border-transparent'
    }`}>
      {/* Header */}
      <button
        onClick={onToggleExpand}
        className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-white/5 transition text-left"
      >
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <div className={`p-2 rounded-full flex-shrink-0 ${
            isFestival ? 'bg-orange-500/20' : 'bg-brand/20'
          }`}>
            {isFestival ? (
              <Tent className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
            ) : (
              <Building className="w-4 h-4 sm:w-5 sm:h-5 text-brand" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={`font-semibold text-base sm:text-lg truncate ${isPast ? 'text-text-muted' : ''}`}>
                {gig.venue}
              </h3>
              {!isPast && (gig.venueId || gig.festivalId) && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  isFestival ? 'bg-orange-500/20 text-orange-400' : 'bg-green-500/20 text-green-400'
                }`}>
                  Linked
                </span>
              )}
            </div>
            <p className="text-xs sm:text-sm text-text-subtle">
              {new Date(gig.date).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
              {gig.city && ` • ${gig.city}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`Delete gig "${gig.venue}"?`)) onDelete(gig.id);
            }}
            className="bg-transparent hover:bg-red-600 transition p-2 rounded-full"
          >
            <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 text-text-subtle" />
          </button>
          {isExpanded
            ? <ChevronUp className="w-5 h-5 text-text-subtle" />
            : <ChevronDown className="w-5 h-5 text-text-subtle" />
          }
        </div>
      </button>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-surface-border-light p-4 sm:p-6 space-y-6">

          {/* Venue / Festival Details */}
          {(gig.website || gig.address || gig.contactPerson || gig.contactEmail || gig.contactPhone) && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2 text-text-muted">
                {isFestival ? <Tent className="w-4 h-4" /> : <Building className="w-4 h-4" />}
                {isFestival ? 'Festival' : 'Venue'} Details
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {gig.website && (
                  <div>
                    <p className="text-xs text-text-subtle">Website</p>
                    <a
                      href={gig.website.startsWith('http') ? gig.website : `https://${gig.website}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-brand-aqua hover:underline truncate block"
                    >{gig.website}</a>
                  </div>
                )}
                {gig.address && (
                  <div>
                    <p className="text-xs text-text-subtle">Address</p>
                    <p className="text-text-muted">{gig.address}</p>
                  </div>
                )}
                {gig.contactPerson && (
                  <div>
                    <p className="text-xs text-text-subtle">Contact</p>
                    <p className="text-sm text-text-muted">{gig.contactPerson}</p>
                  </div>
                )}
                {gig.contactEmail && (
                  <div>
                    <p className="text-xs text-text-subtle">Email</p>
                    <a href={`mailto:${gig.contactEmail}`} className="text-sm text-brand-aqua hover:underline">{gig.contactEmail}</a>
                  </div>
                )}
                {gig.contactPhone && (
                  <div>
                    <p className="text-xs text-text-subtle">Phone</p>
                    <a href={`tel:${gig.contactPhone}`} className="text-sm text-text-muted hover:text-brand-aqua">{gig.contactPhone}</a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Other Bands */}
          {gig.bands && gig.bands.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3 text-text-muted">Other Bands</h4>
              <div className="space-y-2">
                {gig.bands.map((band, idx) => (
                  <div key={idx} className="border border-surface-border-light rounded-card-btn p-3">
                    <p className="font-semibold text-sm text-text-muted">{band.name}</p>
                    {band.website && (
                      <a href={band.website} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-aqua hover:underline block">{band.website}</a>
                    )}
                    {band.email && (
                      <a href={`mailto:${band.email}`} className="text-xs text-text-subtle hover:text-text-muted block">{band.email}</a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Setlist */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2 text-text-muted">
              <FileText className="w-4 h-4" /> Setlist
            </h4>
            {gig.setlist ? (
              <div className="border border-surface-border-light rounded-card-btn p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">{gig.setlist.fileName}</p>
                  <p className="text-xs text-text-subtle">
                    {new Date(gig.setlist.uploadDate).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-1">
                  <a
                    href={gig.setlist.fileURL}
                    target="_blank" rel="noopener noreferrer"
                    className="bg-transparent hover:bg-brand transition p-2 rounded-full"
                  >
                    <File className="w-4 h-4" />
                  </a>
                  <a
                    href={gig.setlist.fileURL}
                    download={gig.setlist.fileName}
                    className="bg-transparent hover:bg-brand transition p-2 rounded-full"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ) : (
              <DragDropZone
                onFileDrop={(file) => onSetlistUpload(gig.id, file)}
                accept=".pdf,.doc,.docx,.txt"
                label="Drop setlist here or click to upload"
                disabled={loading || isMobile}
              />
            )}
          </div>

          {/* Photos */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2 text-text-muted">
              <Image className="w-4 h-4" /> Photos
            </h4>
            <DragDropZone
              onFileDrop={(file) => onPhotoUpload(gig.id, file)}
              accept="image/*"
              label="Drop photo here or click to upload"
              disabled={loading || isMobile}
            />
            {photosArray.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                {photosArray.map(photo => (
                  <div key={photo.id} className="relative group">
                    <img src={photo.fileURL} alt={photo.fileName} className="w-full h-24 object-cover rounded-card-btn" />
                    <button
                      onClick={async () => {
                        if (window.confirm('Delete this photo?')) {
                          await onDeletePhoto(gig.id, photo.id, photo.storagePath);
                        }
                      }}
                      className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 p-1 rounded-full opacity-0 group-hover:opacity-100 transition"
                    >
                      <Trash2 className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
